import React from 'react';

const STAGE_SHORT = {
  made: 'Made',
  second: '2nd',
  stated_debate: 'Debate',
  put: 'Put',
  vote: 'Vote',
  announce: 'Done',
};

const MotionBreadcrumbs = ({
  theme,
  currentMotion,
  motionStack = [],
  motionTypes,
  motions = [],
}) => {
  const cardClass =
    theme === 'dark' ? 'bg-gray-800 text-white border border-gray-600' : 'bg-pp-surface text-pp-ink border border-pp-line/80';

  const freshMotion = (m) => {
    if (!m || !motions.length) return m;
    const row = motions.find((x) => x.id === m.id);
    if (!row) return m;
    return {
      ...m,
      text: row.text,
      originalText: row.originalText ?? m.originalText,
      isAmended: row.isAmended ?? m.isAmended,
      amendments: row.amendments ?? m.amendments,
    };
  };

  const getMotionStages = (motion = currentMotion) => {
    const stages = [
      { id: 'made', label: 'Made', short: STAGE_SHORT.made, status: 'pending_second' },
      { id: 'second', label: 'Second', short: STAGE_SHORT.second, status: 'seconded' },
      { id: 'stated_debate', label: 'Stated / debate', short: STAGE_SHORT.stated_debate, status: 'open_debate' },
      { id: 'put', label: 'Put', short: STAGE_SHORT.put, status: 'ready_for_vote' },
      { id: 'vote', label: 'Vote', short: STAGE_SHORT.vote, status: 'voted_passed' },
      { id: 'announce', label: 'Announce', short: STAGE_SHORT.announce, status: 'completed' },
    ];

    if (!motion) return stages;

    const mt = motionTypes?.[motion.type];
    const skipsSecond = mt?.needsSecond === false;
    const skipsDebate =
      mt?.debatable === false ||
      mt?.flowType === 'INFO' ||
      mt?.flowType === 'CHAIR' ||
      mt?.flowType === 'DIRECT_VOTE';

    const getCurrentStageIndex = () => {
      switch (motion.status) {
        case 'pending_second': return 0;
        case 'seconded': return 1;
        case 'stated':
        case 'open_debate': return 2;
        case 'close_debate':
        case 'ready_for_vote': return 3;
        case 'voted_passed':
        case 'voted_failed': return 4;
        case 'completed': return 5;
        default: return 0;
      }
    };

    const currentIndex = getCurrentStageIndex();

    const processedStages = stages.map((stage, index) => {
      const isDisabled =
        (stage.id === 'second' && skipsSecond) ||
        (stage.id === 'stated_debate' && skipsDebate);
      if (stage.id === 'stated_debate') {
        return {
          ...stage,
          isDisabled,
          isActive: motion.status === 'stated' || motion.status === 'open_debate',
          isCompleted: motion.status === 'close_debate' || motion.status === 'ready_for_vote' || motion.status === 'voted_passed' || motion.status === 'voted_failed' || motion.status === 'completed',
          isUpcoming: motion.status === 'pending_second' || motion.status === 'seconded',
        };
      }

      if (stage.id === 'put') {
        return {
          ...stage,
          isDisabled,
          isActive: motion.status === 'close_debate',
          isCompleted: motion.status === 'ready_for_vote' || motion.status === 'voted_passed' || motion.status === 'voted_failed' || motion.status === 'completed',
          isUpcoming: motion.status === 'pending_second' || motion.status === 'seconded' || motion.status === 'stated' || motion.status === 'open_debate',
        };
      }

      if (stage.id === 'vote') {
        return {
          ...stage,
          isDisabled,
          isActive: motion.status === 'ready_for_vote',
          isCompleted: motion.status === 'voted_passed' || motion.status === 'voted_failed' || motion.status === 'completed',
          isUpcoming: motion.status === 'pending_second' || motion.status === 'seconded' || motion.status === 'stated' || motion.status === 'open_debate' || motion.status === 'close_debate',
        };
      }

      return {
        ...stage,
        isDisabled,
        isActive: index === currentIndex + 1,
        isCompleted: index <= currentIndex,
        isUpcoming: index > currentIndex + 1,
      };
    });

    return processedStages;
  };

  const voteTag = (mt) => {
    if (!mt) return '';
    if (mt.voteRequired === '2/3' || mt.voteRequired === 'TWO_THIRDS') return '2/3';
    if (mt.voteRequired === 'unanimous' || mt.voteRequired === 'UNANIMOUS') return 'Unanimous';
    if (mt.voteRequired === 'chair_decides' || mt.voteRequired === 'CHAIR_DECIDES') return 'Chair';
    if (mt.voteRequired === 'none' || mt.voteRequired === 'NONE') return 'No vote';
    return 'Majority';
  };

  const CharacteristicPills = ({ mt, dimmed }) => {
    if (!mt) return null;
    const pill =
      'inline-flex shrink-0 items-center rounded-full px-1.5 py-px text-[10px] font-semibold ring-1';
    return (
      <div className={`flex flex-wrap gap-1 ${dimmed ? 'opacity-55' : ''}`}>
        <span className={`${pill} bg-pp-accentSoft text-pp-accent ring-pp-accent/25`}>{mt.class}</span>
        <span className={`${pill} ${mt.needsSecond ? 'bg-emerald-50 text-emerald-800 ring-emerald-200' : 'bg-pp-paper text-pp-muted ring-pp-line'}`}>
          {mt.needsSecond ? 'Second' : 'No second'}
        </span>
        <span className={`${pill} ${mt.debatable ? 'bg-amber-50 text-amber-900 ring-amber-200/80' : 'bg-pp-paper text-pp-muted ring-pp-line'}`}>
          {mt.debatable ? 'Debatable' : 'Not debatable'}
        </span>
        <span className={`${pill} ${mt.amendable ? 'bg-violet-50 text-violet-900 ring-violet-200/80' : 'bg-pp-paper text-pp-muted ring-pp-line'}`}>
          {mt.amendable ? 'Amendable' : 'Not amendable'}
        </span>
        <span
          className={`${pill} ${
            voteTag(mt) === '2/3' ? 'bg-rose-50 text-rose-900 ring-rose-200/80' : 'bg-pp-paper text-pp-muted ring-pp-line'
          }`}
        >
          Vote: {voteTag(mt)}
        </span>
      </div>
    );
  };

  const StageRow = ({ motion, dimmed }) => {
    const stages = getMotionStages(motion);
    return (
      <div className={`flex w-full items-center gap-0.5 overflow-x-auto ${dimmed ? 'opacity-50' : ''}`}>
        {stages.map((stage, index) => (
          <React.Fragment key={stage.id}>
            <div className="flex shrink-0 items-center gap-1">
              <div
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-bold ${
                  stage.isDisabled
                    ? 'border-pp-line bg-pp-paper text-pp-muted opacity-40'
                    : stage.isCompleted
                    ? 'border-pp-sage bg-pp-sageSoft text-pp-sage'
                    : stage.isActive
                      ? 'border-pp-accent bg-pp-accentSoft text-pp-accent'
                      : 'border-pp-line bg-pp-paper text-pp-muted'
                }`}
                title={stage.label}
              >
                {stage.isDisabled ? '—' : stage.isCompleted ? '✓' : index + 1}
              </div>
              <span
                className={`text-xs font-semibold ${
                  stage.isDisabled
                    ? 'text-pp-muted opacity-50'
                    : stage.isCompleted
                      ? 'text-pp-sage'
                      : stage.isActive
                        ? 'text-pp-accent'
                        : 'text-pp-muted'
                }`}
              >
                {stage.short}
              </span>
            </div>
            {index < stages.length - 1 && (
              <div
                className={`mx-0.5 h-0.5 min-w-[0.5rem] flex-1 ${
                  stage.isDisabled ? 'bg-pp-line/50' : stage.isCompleted ? 'bg-pp-sage' : 'bg-pp-line'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const CompactStrip = ({ motion, subtitle, emphasize, dimmed, showMeta = true }) => {
    const m = freshMotion(motion);
    const mt = motionTypes[m.type];
    return (
      <div
        className={`rounded-lg border px-2.5 py-1.5 ${
          emphasize
            ? 'border-pp-accent/30 bg-pp-accentSoft/60'
            : dimmed
              ? 'border-pp-line/60 bg-pp-paper/40 opacity-80'
              : 'border-pp-line/80 bg-pp-paper/25'
        }`}
      >
        <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className={`text-[10px] font-semibold uppercase tracking-wide ${emphasize ? 'text-pp-accent' : 'text-pp-muted'}`}>
              {subtitle}
            </span>
            {m?.isAmended && (
              <span className="rounded bg-pp-sageSoft px-1.5 py-px text-[10px] font-semibold text-pp-sage">Amended</span>
            )}
            <p
              className={`min-w-0 flex-1 line-clamp-1 text-sm font-semibold leading-snug ${dimmed ? 'text-pp-muted' : 'text-pp-ink'}`}
              title={m?.text}
            >
              {m?.text ? `“${m.text}”` : '—'}
            </p>
            {showMeta && m?.member && (
              <span className="shrink-0 text-xs text-pp-muted">
                {m.member}
                {m.secondedBy ? ` · Second: ${m.secondedBy}` : ''}
              </span>
            )}
          </div>
          {mt && <CharacteristicPills mt={mt} dimmed={dimmed} />}
        </div>
        <div className="mt-1.5">
          <StageRow motion={m} dimmed={dimmed} />
        </div>
      </div>
    );
  };

  const isSecondaryMotionActive = currentMotion && currentMotion.type !== 'MAIN';

  if (!currentMotion) {
    return null;
  }

  // motionStack is bottom-to-top (index 0 = main motion, last = most recently paused
  // motion), so this renders one "on hold" card per stack entry — the main motion plus
  // any pending amendment(s) it's nested under — followed by the live current motion.
  // A stack of just [main] + a pending amendment renders exactly as before; a deeper
  // stack (e.g. main + a pending primary amendment, with a secondary amendment current)
  // now gets its own card instead of being silently hidden.
  return (
    <div className={`${cardClass} mb-2 rounded-xl p-2 shadow-pp`} data-tour="motion-breadcrumbs">
      <div className="flex flex-col gap-1.5">
        {isSecondaryMotionActive &&
          motionStack.map((m) => (
            <CompactStrip
              key={m.id}
              motion={m}
              subtitle={m.type === 'MAIN' ? 'Main · on hold' : `${motionTypes[m.type]?.name || m.type} · on hold`}
              emphasize={false}
              dimmed
            />
          ))}

        {isSecondaryMotionActive && currentMotion && (
          <CompactStrip
            motion={currentMotion}
            subtitle={`Pending · ${motionTypes[currentMotion.type]?.name || currentMotion.type}`}
            emphasize
            dimmed={false}
          />
        )}

        {!isSecondaryMotionActive && currentMotion && (
          <CompactStrip motion={currentMotion} subtitle="Main motion" emphasize={false} dimmed={false} />
        )}
      </div>
    </div>
  );
};

export default MotionBreadcrumbs;
